// ─────────────────────────────────────────────────────────────────────────────
// Journey scroll constants — kept here (NOT in CameraRig.tsx) so that
// IntroLogoBreak and the mobile feed components can read them without
// pulling Three.js / @react-three/fiber into the mobile bundle. Importing
// a single constant from a file that ALSO exports the CameraRig component
// (which transitively imports the entire R3F + three runtime) was dragging
// ~260KB of unused JS onto the mobile path.
// ─────────────────────────────────────────────────────────────────────────────

/** Last station distance below which an exhibit counts as "parked". */
export const PARKED_THRESHOLD = 10.5;

/** Distance threshold used by Exhibit to decide if it has arrived. */
export const ARRIVAL_THRESHOLD = 8;

/** Viewport heights of scroll dedicated to the intro logo break. */
export const INTRO_PHASE_VH = 100;

// Fraction of the logo mount's time-based `mountProgress` window during
// which the BLOB lerps to the dot slot. The remaining (1 − this) is
// reserved for the W fragments to cascade in around the parked blob.
// Tuned so the blob has visibly arrived at the dot before any fragment
// fades in — the W is never seen sliding over a still-moving blob.
// Consumed by: components/journey/IntroLogoMount.tsx (W phase gate),
// components/sections/TrailingBlobCanvas.tsx (blob travel remap).
export const LOGO_MOUNT_PHASE_SPLIT = 0.55;

// Central motion tokens — every duration, delay, stagger, ease, phase, and
// travel distance the animation system uses lives here. Tune one value and
// every component that consumed it updates in lockstep on next reload.
//
// Anything that should "feel" the same across the site (e.g. snap pace,
// reveal cascade, hover transitions) reads from this file. Anything truly
// intrinsic to a component (e.g. internal layout maths) stays inline.
//
// CSS-side counterparts live in `app/globals.css` as custom properties under
// the `:root` block (--duration-*, --ease-*). Keep both sides in sync when
// tuning shared values.
//
// ─── Map: token → file ──────────────────────────────────────────────────
//   EASE_OUT_QUART          → components/sections/ChapterStation.tsx
//   easeOutCubic            → components/journey/CameraRig.tsx
//                             components/journey/Exhibit.tsx
//                             components/showcase/ProjectZoomShowcase.tsx
//                             hooks/useInfoHijack.ts
//   easeInOutCubic          → components/journey/Exhibit.tsx
//   HIJACK.journey          → components/journey/CameraRig.tsx
//   HIJACK.info             → hooks/useInfoHijack.ts
//   HIJACK.sidebar          → components/journey/ScrollProgress.tsx
//   AUTOSNAP                → components/journey/CameraRig.tsx
//   MOBILE_SNAP             → components/journey/CameraRig.tsx
//   CHAPTER_REVEAL          → components/sections/ChapterStation.tsx
//   HEADLINE_WIPE           → components/sections/ChapterStation.tsx
//   INTRO_HUD               → components/journey/IntroScene.tsx
//   CORRIDOR_REVEAL         → components/journey/Corridor.tsx
//   INTRO_LOGO_PHASES       → components/journey/IntroLogoBreak.tsx
//   EXHIBIT_MOTION          → components/journey/Exhibit.tsx
//   BLOB_MOTION             → components/sections/TrailingBlob.tsx
//                             components/journey/EndPortal.tsx
//   ATMOSPHERE_HANDOFF      → components/journey/Atmosphere.tsx
//   ATMOSPHERE_STARS        → components/journey/IntroAtmosphere.tsx
//   CURSOR                  → components/shared/CustomCursor.tsx
//   FEED_REVEAL             → components/showcase/ProjectFeed.tsx
// ────────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────────────
// Easings
// ─────────────────────────────────────────────────────────────────────────────

// Cubic-bezier deceleration used by motion/react variants (accepts the
// 4-tuple form). Smooth, slightly assertive arrival.
// Consumed by: ChapterStation (lead + tail line reveal).
export const EASE_OUT_QUART = [0.22, 1, 0.76, 1] as const;

// power3.out as a plain function — Lenis takes an easing callback, not a name.
// Consumed by: CameraRig (journey hijack travel), useInfoHijack (info-section
// hijack travel), Exhibit (lateral slide-out + scale-in).
export const easeOutCubic = (t: number): number => 1 - Math.pow(1 - t, 3);

// Cubic inOut for object-arrival motion. Smoother middle than expoInOut —
// keeps the "slow start, settle at the end" feel without the punchy middle
// that reads as unnatural in the journey reveals.
// Consumed by: Exhibit (vertical rise from behind the floor horizon).
export const easeInOutCubic = (t: number): number =>
  t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

// ─────────────────────────────────────────────────────────────────────────────
// Scroll hijack pacing
// One wheel/touch/key gesture = one programmatic scrollTo. These numbers
// control how that step feels: faster travel = more decisive, larger
// inertia tail = stronger swallow of trackpad momentum.
// ─────────────────────────────────────────────────────────────────────────────

export const HIJACK = {
  // CameraRig — station-to-station glide through the 3D corridor.
  journey: {
    travelSeconds: 1,
    wheelDeadZonePx: 0,
    inertiaTailMs: 50,
    touchMinDeltaPx: 0,
  },
  // useInfoHijack — chapter-to-chapter snap in the InfoSection. Tuned for
  // instant response: even a tiny wheel/touch tick triggers the snap, the
  // travel is short, and there's no reveal lock holding the user between
  // steps. Chapter reveal animations are decoupled from the hijack — if
  // the user is wheeling fast the text may not finish the wipe, but the
  // hijack snap stays responsive.
  info: {
    travelSeconds: 0.45,
    wheelDeadZonePx: 0,
    inertiaTailMs: 120,
    touchMinDeltaPx: 4,
    revealLockMs: 0,
  },
  // ScrollProgress sidebar — clicking a chip jumps the page through Lenis.
  // Slightly longer than `journey.travelSeconds` so the manual nav reads as
  // a "guided" tour instead of a hijack snap.
  sidebar: {
    travelSeconds: 1.4,
  },
  // Intro "explore here" CTA — travels from the top of the page through the
  // entire logo-break window to the first exhibit. Deliberately slow (much
  // longer than a sidebar jump) so the user watches the W explode and drift
  // instead of it flashing past on a fast scroll.
  introDescent: {
    travelSeconds: 3,
  },
} as const;

// Autosnap — the hijack engages on intro exit, dragging the camera onto
// station 1 once the user has scrolled `triggerRatio` through the explosion
// window. Lerp is intentionally slower than the regular hijack so the W's
// debris has time to fade before the first exhibit arrives.
// Consumed by: CameraRig (autoSnapTick inside the hijack effect).
export const AUTOSNAP = {
  triggerRatio: 0.45,
  durationSeconds: 2.8,
} as const;

// Mobile snap fallback — when the directional hijack is bypassed (forced
// mobile tier) ScrollTrigger handles snapping with a cooperative duration
// range and a small dwell so taps don't accidentally retrigger the snap.
// Consumed by: CameraRig (ScrollTrigger.snap when `useHijack` is false).
export const MOBILE_SNAP = {
  durationMinSeconds: 2.5,
  durationMaxSeconds: 4.5,
  delaySeconds: 0.5,
  ease: 'sine.inOut',
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// Text reveals
// ─────────────────────────────────────────────────────────────────────────────

// ChapterStation — slow staggered cascade of headline → lead → tail
// triggered once the chapter scrolls into view (= the hijack snap has parked
// the blob and the station is visible). headlineDelay is the hold AFTER the
// station enters view before the wipe starts.
//
// Lead and tail intentionally diverge in three ways so the cascade doesn't
// read as a uniform cadence:
//   • Gaps accelerate — lead arrives 0.42s after the headline finishes
//     starting, tail trails the lead by 0.68s (not the previous flat 0.76s).
//   • Travel distance differs — tail rises from further down with a
//     stronger blur, so the deeper element clearly takes longer to focus.
//   • Eases differ — the lead uses the quick-arriving EASE_OUT_QUART;
//     the tail uses a softer expo-out that lingers at the end, settling
//     the cascade instead of stopping flat.
export const CHAPTER_REVEAL = {
  headlineDelaySeconds: 0.32,
  leadDelaySeconds: 0.74,
  tailDelaySeconds: 1.42,
  durationSeconds: 0.65,
  leadOffsetPx: 18,
  tailOffsetPx: 34,
  leadBlurPx: 8,
  tailBlurPx: 12,
  tailEase: [0.16, 1, 0.3, 1] as const,
} as const;

// Headline mask wipe — the title clip-path inset opens from one side
// (matching the chapter's text alignment) to reveal the words as if a
// curtain were pulling back. ease is a quick-start / slow-finish bezier
// so the curtain "settles" at the end instead of stopping flat.
// Consumed by: ChapterStation (headlineWipeVariants).
export const HEADLINE_WIPE = {
  durationSeconds: 0.95,
  ease: [0.16, 1, 0.3, 1] as const,
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// Intro scene — title / tagline / scroll-cue tweens that appear over the
// IntroLogoBreak overlay the instant the journey mounts.
// Consumed by: IntroScene.
// ─────────────────────────────────────────────────────────────────────────────

// Title and tagline travel different distances over different durations
// — the tagline is lighter and resolves sooner, so it doesn't move in
// lockstep with the heavier title. Bounce amplitude is 9px (not the
// previous 10) so the cue's resting pulse doesn't read as a clean grid.
export const INTRO_HUD = {
  title:       { fromY: 22, durationSeconds: 1.15, ease: 'expo.out' },
  tagline:     { fromY: 13, durationSeconds: 0.95, delaySeconds: 0.18, ease: 'expo.out' },
  // Scroll-cue ↓ bounces forever (yoyo) until the user starts scrolling.
  cueBounce:   { amplitudePx: 9, durationSeconds: 1.15, ease: 'sine.inOut' },
  // Fade the tagline + cue out the moment the user starts moving — fixes the
  // "this stuff is in my way" feel during the explosion.
  exitFade:    { durationSeconds: 0.5, ease: 'power2.in', scrollThresholdPx: 20 },
} as const;

// Corridor floor fade-in — every material in the floor group is lifted from
// opacity 0 to 1 on mount so the floor materialises instead of popping in.
// Consumed by: Corridor.
export const CORRIDOR_REVEAL = {
  durationSeconds: 1.4,
  ease: 'expo.out',
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// IntroLogoBreak phases — the 0→1 progress through INTRO_PHASE_VH is
// segmented into four overlapping phases: approach, burst, fade, breather.
// Tune these to retime the explosion without touching component code.
// ─────────────────────────────────────────────────────────────────────────────

export const INTRO_LOGO_PHASES = {
  approachEnd: 0.3,
  burstEnd: 0.6,
  fadeStart: 0.7,
  fadeEnd: 0.9,
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// Exhibit reveal windows — distances in world units (camera→exhibit Z) over
// which each transform happens. Smaller window = snappier; larger = more
// cinematic build.
// ─────────────────────────────────────────────────────────────────────────────

export const EXHIBIT_MOTION = {
  // Lateral slide-out as the camera leaves a station.
  slideStartDz: 9,
  slideEndDz: 3,
  // Vertical rise from below the floor horizon as the camera approaches.
  revealFarDz: 32,
  revealNearDz: 9,
  revealDropY: 4,
  // Scale-in from SCALE_MIN to 1 as the camera closes in. Runs in parallel
  // with the iframe cross-fade so the exhibit "approaches" instead of fades.
  scaleFarDz: 22,
  scaleNearDz: 9,
  scaleMin: 0.45,
  // Mouse parallax — additive yaw/pitch on top of the resting orientation.
  mouseYawAmp: 0.09,
  mousePitchAmp: 0.045,
  mouseFollowLerp: 0.06,
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// Trailing blob continuous motion
// ─────────────────────────────────────────────────────────────────────────────

// TrailingBlob — gentle, never-ending tumble while the blob is parked at any
// station. Multiplied by `state.clock.elapsedTime` so the blob "breathes"
// independently of scroll.
export const BLOB_MOTION = {
  rotationYPerSecond: 0.8,
  rotationXPerSecond: 0.18,
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// Atmosphere response to the EndPortal handoff — during the orbit-gallery
// scroll window the journey camera is parked, so without this the particle
// motes freeze (their idle sine drift has a period of minutes). Scroll
// progress swirls the field around the portal axis and pulls it toward the
// camera, so the dust visibly answers the same scroll that spins the orbit.
// ─────────────────────────────────────────────────────────────────────────────

export const ATMOSPHERE_HANDOFF = {
  // Total rotation of the particle field across the full handoff scroll.
  swirlRadians: Math.PI * 0.8,
  // World units the field slides toward the camera across the same range.
  zPullUnits: 6,
  // Exponential smoothing rate for the scroll-event-driven progress —
  // same technique as the orbit gallery's smoothing (TrailingBlobCanvas).
  smoothRate: 10,
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// Intro atmosphere — sparse twinkling starfield rendered behind the W mark.
// Per-star delay and duration are sampled inside these ranges so no two
// stars pulse in lockstep.
// ─────────────────────────────────────────────────────────────────────────────

export const ATMOSPHERE_STARS = {
  count: 28,
  delayMinSeconds: 0,
  delayMaxSeconds: 6,
  durationMinSeconds: 4,
  durationMaxSeconds: 9,
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// Custom cursor — pointer-tracking ring that swells over interactive
// elements. Only the rAF lerp lives here; CSS handles hover/press states.
// ─────────────────────────────────────────────────────────────────────────────

export const CURSOR = {
  // Fraction of the remaining distance covered per frame AT 60fps. The
  // consumer time-normalizes this (see CustomCursor), so the ring closes
  // in at the same speed when the frame rate dips — without that, heavy
  // GPU moments (e.g. the EndPortal orbit window) made the cursor itself
  // feel sluggish.
  followLerp: 0.22,
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// ProjectFeed reveal cascade — when a card intersects the viewport its
// `shown` flip is deferred by `min(index, staggerMaxIndex) * staggerStepMs`,
// so the first few cards arrive as a cascade instead of popping in unison.
// The cap prevents cards deep in the feed from inheriting a long lead-in
// once the user has scrolled past the initial batch.
// ─────────────────────────────────────────────────────────────────────────────

export const FEED_REVEAL = {
  staggerStepMs: 200,
  staggerMaxIndex: 3,
} as const;
